/**
 * "Bölge Öner" — classical region proposal over the photographed page
 * (VS-SLICE-9, ADR-0006 follow-up).
 *
 * The user asked for "kullanıcı veya model lasso yapsın": the selection box
 * must be proposable by an algorithm and then refined by hand. This module is
 * the proposal half — deliberately classical and model-free:
 *
 *   luminance -> Otsu threshold -> binary ink mask
 *   -> one dilation pass (only to decide which marks belong together)
 *   -> connected components (8-connected BFS)
 *   -> stats re-measured on the *original* ink pixels, so the reported box is
 *      tight around real content, not around the dilation halo
 *   -> filtered by size and density, capped by count, densest first.
 *
 * It proposes *where* content is, not *what* it is; the user (and, later, the
 * refinement stage) decides what becomes ink. Coordinates are in the same
 * pixel grid the luminance came in on; `scaleRectToSource` maps a downsampled
 * analysis back to native pixels.
 */

export interface RegionProposalOptions {
  /** Grid step used to downsample before analysis (px). */
  stride?: number;
  /** Dilation radius (cells) used only to group marks that belong together. */
  mergeRadius?: number;
  /** Components smaller than this on both axes are dropped (cells). */
  minSize?: number;
  /** Components whose ink density (original ink cells / bbox cells) is below this are dropped. */
  minInkRatio?: number;
  /** Cap on returned regions, densest first. */
  maxRegions?: number;
  /** Pad the tight bbox by this many cells (then scale to source). */
  paddingCells?: number;
  /**
   * Drop components that touch the analysis-frame border. A photographed page
   * usually sits on a darker desk; Otsu marks that desk as ink and it becomes
   * one giant border-touching component that swallows the whole photo. The
   * proposal wants content regions, so the background frame is excluded.
   */
  excludeBorderTouch?: boolean;
}

export interface ProposedRegion {
  x: number;
  y: number;
  width: number;
  height: number;
  inkPixels: number;
  inkRatio: number;
}

const DEFAULTS: Required<RegionProposalOptions> = {
  stride: 1,
  mergeRadius: 1,
  minSize: 6,
  minInkRatio: 0.12,
  maxRegions: 6,
  paddingCells: 0,
  excludeBorderTouch: true,
};

/** RGBA -> luminance (BT.601), array of length width*height. */
export function luminanceFromImageData(
  rgba: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number
): Uint8Array {
  const out = new Uint8Array(width * height);
  for (let i = 0, o = 0; i < out.length; i++, o += 4) {
    out[i] = Math.round(0.299 * rgba[o] + 0.587 * rgba[o + 1] + 0.114 * rgba[o + 2]);
  }
  return out;
}

function otsu(histogram: Uint32Array, total: number): number {
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * histogram[i];
  let sumB = 0;
  let wB = 0;
  let maxVar = -1;
  let threshold = 128;
  for (let t = 0; t < 256; t++) {
    wB += histogram[t];
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;
    sumB += t * histogram[t];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const variance = wB * wF * (mB - mF) * (mB - mF);
    if (variance > maxVar) {
      maxVar = variance;
      threshold = t;
    }
  }
  return threshold;
}

interface Grid {
  width: number;
  height: number;
  ink: Uint8Array;
}

function buildGrid(luminance: Uint8Array, width: number, height: number, stride: number): Grid {
  const gw = Math.max(1, Math.floor((width + stride - 1) / stride));
  const gh = Math.max(1, Math.floor((height + stride - 1) / stride));

  const histogram = new Uint32Array(256);
  const samples = new Uint8Array(gw * gh);
  for (let y = 0; y < gh; y++) {
    const sy = Math.min(height - 1, y * stride);
    for (let x = 0; x < gw; x++) {
      const sx = Math.min(width - 1, x * stride);
      const v = luminance[sy * width + sx];
      samples[y * gw + x] = v;
      histogram[v]++;
    }
  }
  const threshold = otsu(histogram, samples.length);

  const ink = new Uint8Array(gw * gh);
  for (let i = 0; i < samples.length; i++) {
    ink[i] = samples[i] <= threshold ? 1 : 0;
  }
  return { width: gw, height: gh, ink };
}

function dilate(grid: Grid, radius: number): Uint8Array {
  if (radius <= 0) return Uint8Array.from(grid.ink);
  const { width, height, ink } = grid;
  const out = new Uint8Array(ink.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (!ink[y * width + x]) continue;
      for (let dy = -radius; dy <= radius; dy++) {
        const ny = y + dy;
        if (ny < 0 || ny >= height) continue;
        for (let dx = -radius; dx <= radius; dx++) {
          const nx = x + dx;
          if (nx < 0 || nx >= width) continue;
          out[ny * width + nx] = 1;
        }
      }
    }
  }
  return out;
}

interface Component {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  inkCells: number;
}

/**
 * Label the dilated mask, then re-measure every component's bbox and ink count
 * on the ORIGINAL ink pixels that fall inside the same label. The dilation is
 * grouping evidence only; it never inflates the reported geometry.
 */
function componentsWithTightStats(grid: Grid, dilated: Uint8Array): Component[] {
  const { width, height, ink } = grid;
  const labels = new Int32Array(ink.length).fill(-1);
  const components: Component[] = [];
  const queue: number[] = [];

  for (let start = 0; start < dilated.length; start++) {
    if (!dilated[start] || labels[start] !== -1) continue;
    const label = components.length;
    queue.length = 0;
    queue.push(start);
    labels[start] = label;

    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    let inkCells = 0;

    while (queue.length) {
      const idx = queue.pop() as number;
      const x = idx % width;
      const y = Math.floor(idx / width);

      // Stats come from original ink only.
      if (ink[idx]) {
        inkCells++;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }

      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
          const nIdx = ny * width + nx;
          if (dilated[nIdx] && labels[nIdx] === -1) {
            labels[nIdx] = label;
            queue.push(nIdx);
          }
        }
      }
    }

    if (inkCells > 0 && maxX >= minX) {
      components.push({ minX, minY, maxX, maxY, inkCells });
    }
  }
  return components;
}

/**
 * Propose regions on a luminance buffer. Coordinates are returned on the same
 * pixel grid the luminance came in on; use `scaleRectToSource` to map back to
 * native pixels when the analysis ran on a downsampled copy.
 */
export function proposeRegionsFromLuminance(
  luminance: Uint8Array,
  width: number,
  height: number,
  options: RegionProposalOptions = {}
): ProposedRegion[] {
  if (width <= 0 || height <= 0 || luminance.length < width * height) return [];
  const opts = { ...DEFAULTS, ...options };

  const stride = Math.max(1, Math.floor(opts.stride));
  const grid = buildGrid(luminance, width, height, stride);
  const dilated = dilate(grid, opts.mergeRadius);
  const components = componentsWithTightStats(grid, dilated);

  const regions: ProposedRegion[] = [];
  for (const c of components) {
    const boxW = c.maxX - c.minX + 1;
    const boxH = c.maxY - c.minY + 1;

    // A component touching the analysis border is very likely the background
    // frame (desk/table), not content the user wants to convert.
    if (
      opts.excludeBorderTouch &&
      (c.minX === 0 || c.minY === 0 || c.maxX === grid.width - 1 || c.maxY === grid.height - 1)
    ) {
      continue;
    }

    if (boxW < opts.minSize && boxH < opts.minSize) continue;
    const boxCells = boxW * boxH;
    const inkRatio = boxCells > 0 ? c.inkCells / boxCells : 0;
    if (inkRatio < opts.minInkRatio) continue;

    const pad = opts.paddingCells;
    let x = Math.max(0, (c.minX - pad) * stride);
    let y = Math.max(0, (c.minY - pad) * stride);
    let w = (boxW + pad * 2) * stride;
    let h = (boxH + pad * 2) * stride;
    w = Math.max(1, Math.min(w, width - x));
    h = Math.max(1, Math.min(h, height - y));
    regions.push({ x, y, width: w, height: h, inkPixels: c.inkCells, inkRatio });
  }

  regions.sort((a, b) => b.inkPixels - a.inkPixels);
  return regions.slice(0, Math.max(1, opts.maxRegions));
}

/** Map a proposal box (measured on a possibly downsampled grid) to native pixels. */
export function scaleRectToSource(
  rect: { x: number; y: number; width: number; height: number },
  gridWidth: number,
  gridHeight: number,
  sourceWidth: number,
  sourceHeight: number
): { x: number; y: number; width: number; height: number } {
  if (gridWidth <= 0 || gridHeight <= 0) return { x: 0, y: 0, width: 0, height: 0 };
  const sx = sourceWidth / gridWidth;
  const sy = sourceHeight / gridHeight;
  let x = Math.round(rect.x * sx);
  let y = Math.round(rect.y * sy);
  let width = Math.round(rect.width * sx);
  let height = Math.round(rect.height * sy);
  x = Math.max(0, Math.min(x, sourceWidth - 1));
  y = Math.max(0, Math.min(y, sourceHeight - 1));
  width = Math.max(1, Math.min(width, sourceWidth - x));
  height = Math.max(1, Math.min(height, sourceHeight - y));
  return { x, y, width, height };
}
